/**
 * Static deployment shim. Inert unless the build sets VITE_STATIC_MODE=true.
 *
 * The public build has no backend. `tools/export_static.py` records every GET the API answers
 * into frontend/public/data, and this module points the app's own requests at those files, so a
 * page on the deployed site receives exactly the bytes the local API returned. Nothing here
 * computes, fills in or reshapes a result.
 *
 * What it intercepts, all only when active:
 *   fetch()            /api/... GET -> /data/<slug>, with the recorded content type
 *   <img src>          /api/... (the overlay PNGs the map draws) -> /data/<slug>
 *   <a href> clicks    /api/... (the export links) -> /data/<slug>
 *   EventSource        /api/... -> replays the recorded event stream, then closes
 *
 * A request that was not recorded resolves to 404, so the page follows the "no data" path it
 * already has. A non-GET request resolves to 405 at once: the static copy cannot change anything.
 *
 * THE SLUG RULE is defined in the module docstring of tools/export_static.py and implemented
 * there in Python. slugBase() below is the same rule; both are checked against the same table
 * of cases (`npm run test:slug`, which feeds `python ../tools/export_static.py --slug-cases`
 * into checkSlugCases()).
 */

export interface ManifestPart {
  file: string
  bytes: number
}

export interface ManifestEntry {
  slug: string
  source: string
  content_type: string
  bytes: number
  part_count?: number
  parts?: ManifestPart[]
  /** Float arrays stored with only their non-zero cells; expanded again below. */
  sparse?: Record<string, { encoding: string; cells: number; nonzero: number }>
  expanded_bytes?: number
}

interface SparseField {
  _sparse: {
    dtype: 'f32'
    count: number
    nonzero: number
    encoding: 'index-value' | 'bitmask'
    index_dtype?: 'u32'
    index_b64?: string
    mask_b64?: string
    values_b64: string
  }
}

export interface Manifest {
  generated_at: string
  files: Record<string, ManifestEntry>
}

const DATA = '/data'
const MANIFEST_URL = `${DATA}/_manifest.json`
const BODY_HASH_CHARS = 10

/** Steps 0-7 of the slug rule: the manifest key for a request. */
export function slugBase(input: string): string {
  const url = new URL(input, 'http://static.invalid')
  let path = decodePath(url.pathname)
  if (path.startsWith('/api/')) path = path.slice('/api/'.length)
  else if (path === '/api') path = ''

  const pairs = [...new URLSearchParams(url.search)]
  pairs.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : a[1] < b[1] ? -1 : a[1] > b[1] ? 1 : 0))
  const query = pairs.map(([name, value]) => `${name}=${value}`).join('&')
  const joined = query ? `${path}?${query}` : path

  return joined
    .replace(/[^A-Za-z0-9._-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase()
}

/** Step 8 of the slug rule. */
export function extensionFor(contentType: string | null | undefined): string {
  const media = (contentType ?? '').split(';')[0].trim().toLowerCase()
  if (media === 'application/json') return '.json'
  if (media === 'image/png') return '.png'
  return '.bin'
}

export function slugFor(url: string, contentType: string | null | undefined): string {
  return `${DATA}/${slugBase(url)}${extensionFor(contentType)}`
}

/**
 * One request body in the form both sides hash: JSON with object keys sorted by code point, no
 * whitespace, and the browser's own number formatting. Anything that is not JSON hashes as its
 * trimmed text. Defined once in the docstring of tools/export_static.py.
 */
export function canonicalBody(body: string): string {
  try {
    return canonicalValue(JSON.parse(body))
  } catch {
    return body.trim()
  }
}

function canonicalValue(value: unknown): string {
  if (value === null || value === undefined) return 'null'
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : 'null'
  if (typeof value === 'string') return JSON.stringify(value)
  if (Array.isArray(value)) return `[${value.map(canonicalValue).join(',')}]`
  const entries = Object.keys(value as object).sort()
  return `{${entries
    .map((key) => `${JSON.stringify(key)}:${canonicalValue((value as Record<string, unknown>)[key])}`)
    .join(',')}}`
}

async function bodyHash(body: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonicalBody(body)))
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, BODY_HASH_CHARS)
}

/** The manifest key for a request: the slug rule, plus the body hash for a POST. */
export async function requestBase(path: string, method = 'GET', body?: string): Promise<string> {
  const base = slugBase(path)
  if (method.toUpperCase() === 'GET') return base
  return `${base}-${method.toLowerCase()}-${await bodyHash(body ?? '')}`
}

export async function slugForRequest(
  url: string,
  contentType: string | null | undefined,
  method = 'GET',
  body?: string,
): Promise<string> {
  return `${DATA}/${await requestBase(url, method, body)}${extensionFor(contentType)}`
}

/** Used by `npm run test:slug`; returns the number of rows that disagree with Python. */
export async function checkSlugCases(
  cases: { url: string; content_type: string; expected: string; method?: string; body?: string }[],
): Promise<number> {
  let failed = 0
  for (const one of cases) {
    const got = await slugForRequest(one.url, one.content_type, one.method ?? 'GET', one.body)
    if (got !== one.expected) {
      failed += 1
      console.error(`FAIL ${one.url}: got ${got}, expected ${one.expected}`)
    }
  }
  console.log(`slug check (TypeScript): ${cases.length - failed}/${cases.length} cases pass`)
  return failed
}

// --------------------------------------------------------------------------- sparse arrays

/**
 * The export stores a float array as its non-zero cells when that is smaller (a 3-D frame is
 * typically 0.3% wet). Expanding it here rebuilds exactly the base64 the API sent: the cells
 * left out are bit-for-bit zero, and the exporter checked that byte for byte before writing.
 */
function expandSparse(field: SparseField['_sparse']): string {
  const values = new Uint8Array(base64Bytes(field.values_b64))
  const out = new Uint8Array(field.count * 4)
  let written = 0

  const put = (cell: number) => {
    out.set(values.subarray(written * 4, written * 4 + 4), cell * 4)
    written += 1
  }

  if (field.encoding === 'index-value') {
    const index = new Uint32Array(base64Bytes(field.index_b64 ?? ''))
    for (let i = 0; i < index.length; i += 1) put(index[i])
  } else {
    const mask = new Uint8Array(base64Bytes(field.mask_b64 ?? ''))
    for (let cell = 0; cell < field.count; cell += 1) {
      if (mask[cell >> 3] & (1 << (cell & 7))) put(cell)
    }
  }
  return bytesToBase64(out)
}

function base64Bytes(b64: string): ArrayBuffer {
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

function bytesToBase64(bytes: Uint8Array): string {
  // In chunks: String.fromCharCode cannot take a megabyte of arguments at once.
  const CHUNK = 0x8000
  let binary = ''
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK))
  }
  return btoa(binary)
}

export function expandBody(text: string): string {
  const parsed = JSON.parse(text) as Record<string, unknown>
  for (const [name, value] of Object.entries(parsed)) {
    const sparse = (value as SparseField | null)?._sparse
    if (sparse) parsed[name] = expandSparse(sparse)
  }
  return JSON.stringify(parsed)
}

function decodePath(pathname: string): string {
  try {
    return decodeURIComponent(pathname)
  } catch {
    return pathname
  }
}

// --------------------------------------------------------------------------- activation

/**
 * True only in a build made with VITE_STATIC_MODE=true.
 *
 * The exact expression `import.meta.env.VITE_STATIC_MODE` matters: vite substitutes the value
 * for that whole member expression at build time. Writing `import.meta.env?.VITE_STATIC_MODE`
 * instead makes vite substitute the env OBJECT, which came out as `{}` - the flag read
 * undefined and the shim silently did nothing in a static build. The try/catch is for node,
 * where `import.meta.env` does not exist at all (the slug check imports this file directly).
 */
function staticMode(): boolean {
  try {
    return import.meta.env.VITE_STATIC_MODE === 'true'
  } catch {
    return false
  }
}

const ACTIVE = staticMode()

let manifest: Manifest | null = null
let version = ''
const manifestReady: Promise<Manifest | null> = ACTIVE ? loadManifest() : Promise.resolve(null)

async function loadManifest(): Promise<Manifest | null> {
  try {
    const response = await fetch(MANIFEST_URL, { cache: 'no-cache' })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    manifest = (await response.json()) as Manifest
    version = manifest.generated_at
    return manifest
  } catch (cause) {
    // Without the manifest nothing can be resolved. Say so once; every API call then 404s and
    // each page shows its own empty state rather than waiting for a server that is not there.
    console.error(`The static data manifest could not be read (${MANIFEST_URL}):`, cause)
    return null
  }
}

/** The /api path of a same-origin URL, or null if the request is not for the API. */
function apiPath(raw: string): string | null {
  let url: URL
  try {
    url = new URL(raw, window.location.href)
  } catch {
    return null
  }
  if (url.origin !== window.location.origin) return null
  if (url.pathname !== '/api' && !url.pathname.startsWith('/api/')) return null
  return url.pathname + url.search
}

function entryFor(path: string): ManifestEntry | null {
  return manifest?.files[slugBase(path)] ?? null
}

function fileUrl(name: string): string {
  return version ? `${DATA}/${name}?v=${encodeURIComponent(version)}` : `${DATA}/${name}`
}

/** The text of a request body, or null if it is not something a recorded body could match. */
async function requestBodyText(input: RequestInfo | URL, init?: RequestInit): Promise<string | null> {
  const body = init?.body
  if (typeof body === 'string') return body
  if (body === undefined && input instanceof Request) {
    try {
      return await input.clone().text()
    } catch {
      return null
    }
  }
  if (body === null || body === undefined) return ''
  if (body instanceof URLSearchParams) return body.toString()
  return null // FormData, a Blob, a stream: an upload, which no recording can answer
}

/** The rejection the platform itself gives for an aborted request. */
function abortError(): DOMException {
  return new DOMException('The operation was aborted.', 'AbortError')
}

function jsonResponse(status: number, detail: string): Response {
  return new Response(JSON.stringify({ detail }), {
    status,
    headers: { 'Content-Type': 'application/json' },
  })
}

const NOT_RECORDED = 'This page asked for something the static copy does not contain.'
const READ_ONLY = 'This is a static copy of the platform: nothing here can be changed.'

// --------------------------------------------------------------------------- install

if (ACTIVE) {
  install()
}

function install(): void {
  const realFetch = window.fetch.bind(window)

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const request = input instanceof Request ? input : null
    const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
    const path = apiPath(raw)
    if (path === null) return realFetch(input as RequestInfo | URL, init)

    const method = (init?.method ?? request?.method ?? 'GET').toUpperCase()
    await manifestReady

    let entry: ManifestEntry | null
    if (method === 'GET') {
      entry = entryFor(path)
    } else {
      // A POST the export recorded - the alert evaluation and its message templates, the breach
      // preview - is answered from that recording. Everything else changes state, and a static
      // copy cannot: it gets 405, and so does a body nobody recorded.
      const body = await requestBodyText(input, init)
      entry = body === null ? null : manifest?.files[await requestBase(path, method, body)] ?? null
      if (!entry) return jsonResponse(405, READ_ONLY)
    }
    if (!entry) return jsonResponse(404, NOT_RECORDED)

    const signal = init?.signal ?? request?.signal ?? undefined
    const headers = { 'Content-Type': entry.content_type || 'application/octet-stream' }

    // Reading the file to the end here, rather than handing back the live stream, matters: the
    // map aborts a frame request whenever the playhead moves on, and an abort that lands after
    // this resolved would surface to the caller as "Failed to fetch" while it consumed the
    // body. Buffering, and re-throwing an abort under its own name, keeps the failure shape
    // the platform's own: callers that ignore AbortError - the map does - keep ignoring it.
    try {
      if (signal?.aborted) throw abortError()
      let buffer: ArrayBuffer
      if (entry.parts) {
        const pieces: ArrayBuffer[] = []
        for (const part of entry.parts) {
          const piece = await realFetch(fileUrl(part.file), { signal })
          if (!piece.ok) return jsonResponse(piece.status, `${NOT_RECORDED} (${part.file})`)
          pieces.push(await piece.arrayBuffer())
        }
        buffer = await new Blob(pieces).arrayBuffer()
      } else {
        const response = await realFetch(fileUrl(entry.slug), { signal })
        if (!response.ok) return jsonResponse(response.status, `${NOT_RECORDED} (${entry.slug})`)
        buffer = await response.arrayBuffer()
      }
      if (entry.sparse) {
        return new Response(expandBody(new TextDecoder().decode(buffer)), { status: 200, headers })
      }
      return new Response(buffer, { status: 200, headers })
    } catch (cause) {
      throw signal?.aborted ? abortError() : cause
    }
  }

  // The map loads its overlay PNGs by setting an image source, which never goes through fetch.
  // An unrecorded frame becomes an empty source, so the image reports an error the way a failed
  // load does today - no request is made for a file that is not there.
  const rewriteImage = (value: string): string => {
    const path = apiPath(value)
    if (path === null) return value
    const entry = entryFor(path)
    return entry && !entry.parts ? fileUrl(entry.slug) : 'data:,'
  }

  const descriptor = Object.getOwnPropertyDescriptor(HTMLImageElement.prototype, 'src')
  if (descriptor?.set && descriptor.get) {
    const { get, set } = descriptor
    Object.defineProperty(HTMLImageElement.prototype, 'src', {
      configurable: true,
      enumerable: descriptor.enumerable,
      get,
      set(value: string) {
        set.call(this, rewriteImage(String(value)))
      },
    })
  }
  const setAttribute = Element.prototype.setAttribute
  Element.prototype.setAttribute = function (name: string, value: string): void {
    const next = this instanceof HTMLImageElement && name.toLowerCase() === 'src' ? rewriteImage(String(value)) : value
    setAttribute.call(this, name, next)
  }

  // The export links are plain anchors, so they are pointed at the recorded file on the way out.
  document.addEventListener(
    'click',
    (event) => {
      const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null
      if (!anchor) return
      const path = apiPath(anchor.getAttribute('href') ?? '')
      if (path === null) return
      const entry = entryFor(path)
      if (!entry || entry.parts) {
        event.preventDefault()
        return
      }
      anchor.href = fileUrl(entry.slug)
    },
    true,
  )

  // A run's progress stream is a recorded file here: it is replayed once and then closed. No
  // reconnection, because nothing is running.
  const RealEventSource = window.EventSource
  class RecordedEventSource extends EventTarget {
    static readonly CONNECTING = 0
    static readonly OPEN = 1
    static readonly CLOSED = 2
    readonly CONNECTING = 0
    readonly OPEN = 1
    readonly CLOSED = 2
    readonly url: string
    readonly withCredentials = false
    readyState = 0
    onopen: ((event: Event) => unknown) | null = null
    onmessage: ((event: MessageEvent<string>) => unknown) | null = null
    onerror: ((event: Event) => unknown) | null = null

    constructor(url: string) {
      super()
      this.url = url
      void this.replay(url)
    }

    close(): void {
      this.readyState = 2
    }

    private emit(event: Event): void {
      const handler = (this as unknown as Record<string, unknown>)[`on${event.type}`]
      if (typeof handler === 'function') (handler as (e: Event) => unknown).call(this, event)
      this.dispatchEvent(event)
    }

    private async replay(url: string): Promise<void> {
      const response = await window.fetch(url)
      if (this.readyState === 2) return
      if (!response.ok) {
        this.readyState = 2
        this.emit(new Event('error'))
        return
      }
      const text = await response.text()
      if (this.readyState === 2) return
      this.readyState = 1
      this.emit(new Event('open'))
      for (const block of text.split(/\r?\n\r?\n/)) {
        if (this.readyState === 2) return
        let name = 'message'
        let lastEventId = ''
        const data: string[] = []
        for (const line of block.split(/\r?\n/)) {
          const colon = line.indexOf(':')
          if (colon <= 0) continue
          const field = line.slice(0, colon)
          const value = line.slice(colon + 1).replace(/^ /, '')
          if (field === 'event') name = value
          else if (field === 'data') data.push(value)
          else if (field === 'id') lastEventId = value
        }
        if (data.length) {
          this.emit(new MessageEvent(name, { data: data.join('\n'), lastEventId, origin: window.location.origin }))
        }
      }
      this.readyState = 2
    }
  }
  window.EventSource = function (url: string | URL, init?: EventSourceInit) {
    const path = apiPath(String(url))
    return path === null
      ? new RealEventSource(url, init)
      : (new RecordedEventSource(path) as unknown as EventSource)
  } as unknown as typeof EventSource
}
